import type { BorderStyle, CellStyle } from '@/model/cellStyle';
import type { TableStyle } from '@/model/tableStyle';
import type { StyleToMerge } from '@/model/types';
import type { Vector2 } from '@/utils/vec';

export type Actions = {
  undo: undefined;
  redo: undefined;
  delete: undefined;
  escape: undefined;
  highlight: { pos: Vector2; dontClear?: boolean };
  expandHighlight: { delta: Vector2 };
  formatAsTable: undefined;
  borderOutside: BorderStyle | null;
  borderTop: BorderStyle | null;
  borderLeft: BorderStyle | null;
  borderRight: BorderStyle | null;
  borderBottom: BorderStyle | null;
  borderHorizontal: BorderStyle | null;
  borderVertical: BorderStyle | null;
  borderInner: BorderStyle | null;
  borderAll: BorderStyle | null;
  cellStyle: StyleToMerge<CellStyle>;
  tableStyle: StyleToMerge<TableStyle>;
  print: undefined;
};

export type ActionHandler<K extends keyof Actions = keyof Actions> = (
  action: Actions[K],
) => void;

class EventHandler {
  listeners = new Map<string, Set<ActionHandler>>();
  emit<K extends keyof Actions>(
    ...args: Actions[K] extends undefined ? [K] : [K, Actions[K]]
  ): void;
  emit<K extends keyof Actions>(name: K, action?: Actions[K]) {
    const listeners = Array.from(this.listeners.get(name) || []);

    for (const listener of listeners) {
      try {
        listener(action);
      } catch (error) {
        console.error(error);
      }
    }
  }
  on<K extends keyof Actions>(name: K, listener: ActionHandler<K>): () => void {
    const listeners = this.listeners.get(name) || new Set();
    listeners.add(listener as ActionHandler);
    this.listeners.set(name, listeners);
    return () => {
      listeners.delete(listener as ActionHandler);
    };
  }
  ons(stuff: {
    [K in keyof Actions]?: ActionHandler<K>;
  }): () => void {
    const unsubscribes: (() => void)[] = [];
    for (const [name, listener] of Object.entries(stuff)) {
      unsubscribes.push(
        this.on(name as keyof Actions, listener as ActionHandler),
      );
    }
    return () => {
      for (const unsubscribe of unsubscribes) {
        unsubscribe();
      }
    };
  }
}

export const $actionEvent = new EventHandler();
